import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { LoggedUser, User } from './types'
import { call, put, takeEvery } from 'redux-saga/effects'
import { AxiosResponse } from 'axios'
import * as Api from 'services/api'
import { LoginFormType } from 'containers/user/types'

type ErrorTypes = {
  login: string | null
  recoverAccount: string | null
}

type ErrorKeyValueType = {
  key: keyof ErrorTypes
  value: null | string
}

type LoadingKeyValueType = {
  key: keyof UserState['loading']
  value: boolean
}

type UserState = User & {
  loggedIn: Boolean
  needsConfirmation: Boolean
  recoveryEmailSent: Boolean
  error: ErrorTypes
  loading: {
    login: boolean
    recoverAccount: boolean
  }
}

const initialState: UserState = {
  userId: '',
  firstName: '',
  lastName: '',
  email: '',
  roles: [],
  loggedIn: false,
  needsConfirmation: false,
  recoveryEmailSent: false,
  error: {
    login: null,
    recoverAccount: null,
  },
  loading: {
    login: false,
    recoverAccount: false,
  },
}

const slice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    postLogin: (state, action: PayloadAction<any>) => {},
    recoverAccount: (state, action: PayloadAction<any>) => {},
    setRecoveryEmailSent: (state, action: PayloadAction<any>) => {
      state.recoveryEmailSent = true
    },
    setNeedsConfirmation: (state, action: PayloadAction<any>) => {
      state.needsConfirmation = true
      state.firstName = action.payload.firstName
      state.lastName = action.payload.lastName
    },
    setLogin: state => {
      state.loggedIn = true
      if (localStorage.getItem('user')) {
        const user: UserState = JSON.parse(localStorage.getItem('user')!)
        state.userId = user.userId
        state.firstName = user.firstName
        state.lastName = user.lastName
        state.email = user.email
        state.roles = user.roles
      }
    },
    setLoginWithToken: (state, action: PayloadAction<LoggedUser>) => {
      state.loggedIn = true
      state.userId = action.payload.userId
      state.firstName = action.payload.firstName
      state.lastName = action.payload.lastName
      state.email = action.payload.email
      state.roles = action.payload.roles
      localStorage.setItem('token', action.payload.token)
      localStorage.setItem('refreshToken', action.payload.refreshToken)
      localStorage.setItem('user', JSON.stringify(state))
    },
    logout: state => {
      state.loggedIn = false
      state.userId = ''
      state.firstName = ''
      state.lastName = ''
      state.email = ''
      state.roles = []
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
    },

    setLoading: (state, action: PayloadAction<LoadingKeyValueType>) => {
      const { key, value } = action.payload
      state.loading[key] = value
    },
    setLoginError: (state, action: PayloadAction<string>) => {
      state.error.login = action.payload
    },
    setError: (state, action: PayloadAction<ErrorKeyValueType>) => {
      const { key, value } = action.payload
      state.error[key] = value
    },
  },
})

export const actions = slice.actions

export const reducer = slice.reducer

export function* sagas() {
  yield takeEvery(actions.postLogin, postLoginSaga)
  yield takeEvery(actions.recoverAccount, recoverAccountSaga)
}
/**
 * Sagas
 */

const setLoadingTrue = function* (key: LoadingKeyValueType['key']) {
  yield put(actions.setLoading({ key: key, value: true }))
}

const setLoadingFalse = function* (key: LoadingKeyValueType['key']) {
  yield put(actions.setLoading({ key: key, value: false }))
}

export const postLoginSaga = function* (action: PayloadAction<LoginFormType>) {
  try {
    yield setLoadingTrue('login')
    const data = action.payload
    const response: AxiosResponse<any> = yield call(Api.postForm, Api.ENDPOINTS.login, data)
    yield setLoadingFalse('login')
    if (response.data?.status === 'pending') {
      yield put(actions.setNeedsConfirmation(data))
    } else {
      const logindata: LoggedUser = response.data
      yield put(actions.setLoginWithToken(logindata))
    }
  } catch (error: any) {
    yield setLoadingFalse('login')
    let errorMessage = 'An error occurred. Please try again.'
    if (error?.response?.status === 401) {
      errorMessage = 'The email address or password is incorrect. Please try again.'
    } else if (error?.response?.status === 403) {
      errorMessage = error.response.data ? error.response.data : 'Forbidden access'
    }
    yield put(actions.setError({ key: 'login', value: errorMessage }))
    // yield put(actions.setLoginError(errorMessage))
    console.log(error)
  }
}

export const recoverAccountSaga = function* (action: PayloadAction<string>) {
  try {
    yield setLoadingTrue('recoverAccount')
    const email = action.payload
    const response: AxiosResponse<any> = yield call(Api.postForm, Api.ENDPOINTS.accountRecovery, { email: email })
    yield put(actions.setRecoveryEmailSent(true))
    yield setLoadingFalse('recoverAccount')
  } catch (error: any) {
    yield setLoadingFalse('recoverAccount')
    let errorMessage = 'An error occurred. Please try again.'
    yield put(actions.setError({ key: 'recoverAccount', value: errorMessage }))
    console.log(error)
  }
}
